/**
 * Configuration class for Blockdaemon SDK and API clients.
 *
 * @remarks
 * Allows advanced customization of authentication, base path, and HTTP options.
 * This class encapsulates all configuration parameters and provides a clean
 * interface for SDK initialization.
 *
 * @example
 * ```typescript
 * import { SDKConfiguration } from "@blockdaemon/bd-sdk";
 *
 * // Basic configuration
 * const config = new SDKConfiguration({
 *   apiKey: "my-api-key",
 *   basePath: "https://svc.blockdaemon.com"
 * });
 *
 * // Advanced configuration with async authentication
 * const advancedConfig = new SDKConfiguration({
 *   apiKey: async () => {
 *     const response = await fetch("/api/key");
 *     return response.text();
 *   },
 *   basePath: "https://custom-endpoint.blockdaemon.com",
 *   baseOptions: {
 *     timeout: 30000,
 *     headers: {
 *       "User-Agent": "MyApp/1.0",
 *       "X-Custom-Header": "custom-value"
 *     }
 *   }
 * });
 * ```
 */
export class SDKConfiguration {
    /**
     * API key for authentication
     *
     * The API key used to authenticate requests to Blockdaemon services.
     * Supports static strings, Promises, and functions for flexible key management.
     */
    apiKey;
    /**
     * Username for basic authentication
     *
     * Username for HTTP Basic Authentication (legacy method).
     */
    username;
    /**
     * Password for basic authentication
     *
     * Password for HTTP Basic Authentication (legacy method).
     */
    password;
    /**
     * OAuth2 access token
     *
     * OAuth2 access token for authentication. Supports static strings,
     * Promises, and functions for token refresh scenarios.
     */
    accessToken;
    /**
     * Override base path for API requests
     *
     * Custom base URL for API requests, allowing environment-specific endpoints.
     */
    basePath;
    /**
     * Custom HTTP options (e.g., headers, timeouts)
     *
     * Additional HTTP request options for customizing request behavior.
     */
    baseOptions;
    /**
     * Optional TrueUps referral address for StakeWise transactions.
     */
    referralTag;
    /**
     * SDK / API config for each protocol
     */
    protocolConfig;
    /**
     * Creates a new SDKConfiguration instance.
     *
     * @param param - Configuration parameters object
     *
     * @example
     * ```typescript
     * const config = new SDKConfiguration({
     *   apiKey: "your-api-key",
     *   basePath: "https://svc.blockdaemon.com",
     *   baseOptions: { timeout: 30000 }
     * });
     * ```
     */
    constructor(param = {}) {
        this.apiKey = param.apiKey;
        this.username = param.username;
        this.password = param.password;
        this.accessToken = param.accessToken;
        this.basePath = param.basePath;
        this.baseOptions = param.baseOptions;
        this.referralTag = param.referralTag;
        this.protocolConfig = param.protocolConfig;
    }
}
/** Safely get an environment variable value in a cross-environment way. */
function getEnvVar(name) {
    const maybeProcess = typeof process !== "undefined"
        ? process
        : undefined;
    const env = maybeProcess?.env;
    return env ? env[name] : undefined;
}
/**
 * Determine if logging is disabled by environment configuration.
 *
 * Controlled by ENABLE_LOG, default is enabled (returns false).
 * When ENABLE_LOG is "false" or "0", returns true (disabled).
 */
export function isLoggingDisabled() {
    const value = getEnvVar("ENABLE_LOG");
    if (typeof value === "undefined")
        return false;
    return /^(false|0)$/i.test(String(value));
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29uZmlndXJhdGlvbi5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uL3NyYy9tYWluL3NoYXJlZC9jb25maWd1cmF0aW9uLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQTBMQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQWtDRztBQUNILE1BQU0sT0FBTyxnQkFBZ0I7SUFDM0I7Ozs7O09BS0c7SUFDSCxNQUFNLENBSWtDO0lBRXhDOzs7O09BSUc7SUFDSCxRQUFRLENBQVU7SUFFbEI7Ozs7T0FJRztJQUNILFFBQVEsQ0FBVTtJQUVsQjs7Ozs7T0FLRztJQUNILFdBQVcsQ0FJaUQ7SUFFNUQ7Ozs7T0FJRztJQUNILFFBQVEsQ0FBVTtJQUVsQjs7OztPQUlHO0lBQ0gsV0FBVyxDQUEyQjtJQUV0Qzs7T0FFRztJQUNILFdBQVcsQ0FBVTtJQUVyQjs7T0FFRztJQUNILGNBQWMsQ0FBbUI7SUFFakM7Ozs7Ozs7Ozs7Ozs7T0FhRztJQUNILFlBQVksUUFBa0MsRUFBRTtRQUM5QyxJQUFJLENBQUMsTUFBTSxHQUFHLEtBQUssQ0FBQyxNQUFNLENBQUM7UUFDM0IsSUFBSSxDQUFDLFFBQVEsR0FBRyxLQUFLLENBQUMsUUFBUSxDQUFDO1FBQy9CLElBQUksQ0FBQyxRQUFRLEdBQUcsS0FBSyxDQUFDLFFBQVEsQ0FBQztRQUMvQixJQUFJLENBQUMsV0FBVyxHQUFHLEtBQUssQ0FBQyxXQUFXLENBQUM7UUFDckMsSUFBSSxDQUFDLFFBQVEsR0FBRyxLQUFLLENBQUMsUUFBUSxDQUFDO1FBQy9CLElBQUksQ0FBQyxXQUFXLEdBQUcsS0FBSyxDQUFDLFdBQVcsQ0FBQztRQUNyQyxJQUFJLENBQUMsV0FBVyxHQUFHLEtBQUssQ0FBQyxXQUFXLENBQUM7UUFDckMsSUFBSSxDQUFDLGNBQWMsR0FBRyxLQUFLLENBQUMsY0FBYyxDQUFDO0lBQzdDLENBQUM7Q0FDRjtBQUVELDJFQUEyRTtBQUMzRSxTQUFTLFNBQVMsQ0FBQyxJQUFZO0lBQzdCLE1BQU0sWUFBWSxHQUNoQixPQUFPLE9BQU8sS0FBSyxXQUFXO1FBQzVCLENBQUMsQ0FBRSxPQUFtRTtRQUN0RSxDQUFDLENBQUMsU0FBUyxDQUFDO0lBQ2hCLE1BQU0sR0FBRyxHQUFHLFlBQVksRUFBRSxHQUFHLENBQUM7SUFDOUIsT0FBTyxHQUFHLENBQUMsQ0FBQyxDQUFDLEdBQUcsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLENBQUMsU0FBUyxDQUFDO0FBQ3JDLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sVUFBVSxpQkFBaUI7SUFDL0IsTUFBTSxLQUFLLEdBQUcsU0FBUyxDQUFDLFlBQVksQ0FBQyxDQUFDO0lBQ3RDLElBQUksT0FBTyxLQUFLLEtBQUssV0FBVztRQUFFLE9BQU8sS0FBSyxDQUFDO0lBQy9DLE9BQU8sY0FBYyxDQUFDLElBQUksQ0FBQyxNQUFNLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQztBQUM1QyxDQUFDIn0=